// The data arrays the Productivity Chart's own metric series are drawn from: one builder per
// metric, plus the column widths the column series declare. Everything here turns a bucket's
// figures into points; nothing here knows what a series looks like once drawn.

import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import type { ProductivityBucket, ProductivityProductionGroup, WeightSeriesDay } from '@/types/SectionLiveKpi';
import { plottableValue } from './plausibleRange';
import { BUCKET_PERIOD_MS, bucketTimestamp, DAY_MS } from './productivityFormatting';
import { cameraBucketField, type CameraMetric } from './cameraSeries';
import { PRODUCTIVITY_METRICS, waterActualKey, type MetricKey, type WaterActualKey } from './productivityMetrics';

/**
 * A metric's figure as this chart will plot it: itself, or null where the metric has a plausible band and
 * the figure falls outside it. The registry owns which metrics have one, so every site that blanks a
 * non-physical derivation - series, readout row, reference line - asks the same question.
 */
export function productivityPlottableValue(key: MetricKey, value: number | null): number | null {
    const range = PRODUCTIVITY_METRICS[key].plausibleRange;
    return range === null ? value : plottableValue(value, range);
}

/**
 * Markers off along the whole line, on only where a reading would otherwise be invisible.
 *
 * A point with a gap on both sides draws no line segment at all - a segment needs two ends - so without a
 * marker it is simply missing from the plot. The completeness rule makes that ordinary: an aggregate is
 * withheld for any bucket one camera was silent through, so an intermittent camera leaves alternating
 * figures and gaps, and undecorated a Section with readings in half its buckets shows no line at all.
 *
 * The returned array is deliberately mixed - a plain `[x, y]` tuple where nothing special is needed, a
 * point object only for the isolated ones. Tuples stay far cheaper for the great majority.
 */
export function isolatedPointMarkers(data: Array<[number, number | null]>): Array<unknown> {
    return data.map(([x, y], index) => {
        const isolated = y !== null && (data[index - 1]?.[1] ?? null) === null && (data[index + 1]?.[1] ?? null) === null;
        return isolated ? { x, y, marker: { enabled: true, radius: 3 } } : [x, y];
    });
}

/**
 * The buckets that state this metric: the ones of the metric's own grain. Only ever narrows anything
 * at hourly, where the response is deliberately mixed - the hourly buckets weight and water report
 * in, plus one null-hour bucket per day carrying the figures that are published per calendar day.
 *
 * Left unfiltered, that day bucket contributed a second point at every midnight - its own timestamp
 * being that day's midnight, the same instant as the hour-00 bucket - carrying null for every hourly
 * metric. A null point breaks a line, so weight, Growth Index and the water areaspline each showed a
 * gap from 23:00 to 01:00 every night of an hourly range.
 */
function bucketsStating(
    buckets: ProductivityBucket[],
    key: MetricKey,
    granularity: ProductivityGranularity
): ProductivityBucket[] {
    if (granularity !== 'hour') return buckets;
    const statedPerDay = PRODUCTIVITY_METRICS[key].publishedGrain === 'day';
    return buckets.filter((bucket) => (bucket.hour === null) === statedPerDay);
}

// One bucket figure's line data, with an explicit null point inserted just after every closed
// productionGroup's end. Highcharts otherwise draws a straight diagonal between the last point of one
// Production Group and the first of the next; the null point breaks the line there instead, so the
// gap between two Production Groups reads as empty space rather than a series. A bucket the backend
// already returned with a null figure (present day, no contributing sensor) needs no such help -
// that null already breaks the line on its own.
//
// The one implementation of that gap break, for every curve on either chart. Generic in the bucket and
// taking the figure as an accessor, because a camera's buckets are a different shape from the Section's
// while the period-gap rule is the same rule - a second copy of it is a camera curve running a diagonal
// across a boundary the aggregate beside it correctly breaks.
function buildBucketSeriesData<Bucket extends { date: string; hour: string | null }>(
    buckets: Bucket[],
    productionGroups: ProductivityProductionGroup[],
    // The figure to plot, already past whatever plausibility gate it has - see
    // productivityPlottableValue and plausibleRange.ts.
    value: (bucket: Bucket) => number | null
): Array<[number, number | null]> {
    const points: Array<[number, number | null]> = buckets.map((bucket) => [bucketTimestamp(bucket), value(bucket)]);

    for (const productionGroup of productionGroups) {
        if (productionGroup.end !== null) points.push([Date.parse(productionGroup.end) + DAY_MS, null]);
    }

    return points.sort((a, b) => a[0] - b[0]);
}

// Whether any reading in the series has a null (or nothing at all) on both sides of it. Such a
// point has no line segment to appear in, so a line series drawn without markers renders it as
// nothing whatsoever - the series looks like missing data when it is anything but. A Sensor that
// reported on one day between two silent ones produces exactly this.
export function hasIsolatedPoints(data: Array<[number, number | null]>): boolean {
    return data.some((point, index) => point[1] !== null && (data[index - 1]?.[1] ?? null) === null && (data[index + 1]?.[1] ?? null) === null);
}

export function buildWeightSeriesData(
    buckets: ProductivityBucket[],
    productionGroups: ProductivityProductionGroup[],
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    return buildBucketSeriesData(bucketsStating(buckets, 'weight', granularity), productionGroups, (bucket) => bucket.weight_kg);
}

export function buildDailyGainSeriesData(
    buckets: ProductivityBucket[],
    productionGroups: ProductivityProductionGroup[],
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    return buildBucketSeriesData(bucketsStating(buckets, 'dailyGain', granularity), productionGroups, (bucket) => productivityPlottableValue('dailyGain', bucket.daily_gain_g));
}

export function buildGrowthIndexSeriesData(
    buckets: ProductivityBucket[],
    productionGroups: ProductivityProductionGroup[],
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    return buildBucketSeriesData(bucketsStating(buckets, 'growthIndex', granularity), productionGroups, (bucket) => productivityPlottableValue('growthIndex', bucket.growth_index));
}

/**
 * One camera's own data for one metric, built through the same builder the aggregate's is so the two
 * cannot drift: the same plausibility gate, the same null past every closed Production Group, and at
 * hourly the same narrowing to the metric's own grain.
 *
 * Takes a metric a camera actually reports rather than any of the six, so which figure to read is the
 * camera registry's own answer (see cameraBucketField).
 */
export function buildCameraMetricSeriesData(
    cameraBuckets: WeightSeriesDay[],
    productionGroups: ProductivityProductionGroup[],
    key: CameraMetric,
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    const field = cameraBucketField(key);

    // All three of a camera's figures are published per bucket, so this only drops a day-grain bucket a
    // camera list would not have carried anyway. Kept so a camera and the aggregate cannot end up
    // filtered by different rules - see bucketsStating.
    const stated = granularity === 'hour' ? cameraBuckets.filter((bucket) => bucket.hour !== null) : cameraBuckets;

    return buildBucketSeriesData(stated, productionGroups, (bucket) => productivityPlottableValue(key, bucket[field]));
}

export function buildWaterNormSeriesData(
    buckets: ProductivityBucket[],
    productionGroups: ProductivityProductionGroup[],
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    return buildBucketSeriesData(bucketsStating(buckets, 'waterNorm', granularity), productionGroups, (bucket) => bucket.norm_water_per_pig);
}

// Water actual capped at Water Norm: the column drawn on top of the norm column below it (see
// buildWaterOverNormSeriesData for the part above the cap). Capping here - rather than the raw
// actual - is what makes the over-norm highlight explicit instead of the actual column simply
// growing taller and hiding the norm column underneath it, which is the deficit-only reading the
// prototype had.
export function buildWaterActualSeriesData(
    buckets: ProductivityBucket[],
    productionGroups: ProductivityProductionGroup[],
    actualKey: WaterActualKey,
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    // The cap only ever applies at day granularity, since that is the only place a norm exists to
    // cap against - an hourly bucket's norm is null and the reading passes through untouched.
    return buildBucketSeriesData(bucketsStating(buckets, 'water', granularity), productionGroups, (bucket) => {
        const actual = bucket[actualKey];
        return actual !== null && bucket.norm_water_per_pig !== null ? Math.min(actual, bucket.norm_water_per_pig) : actual;
    });
}

// The over-norm highlight: one `columnrange` point per day where actual exceeds norm, spanning
// exactly the excess (norm -> actual) so it draws as a distinct band on top of the actual/norm
// overlap - over-drinking made as visible as a deficit, rather than the actual column simply
// covering the (now shorter) norm column underneath it.
// Only the buckets actually over norm. The other buckets are dropped rather than carried as null
// columnrange points: Highcharts renders one of those as a full-height bar anchored off the top of
// the chart, which the plot-area clip turns into a solid red column standing in whichever band the
// bar's lower edge happens to land in. Dropping them leaves the series with scattered points, whose
// spacing would normally decide the column width - hence waterColumnRange below, which fixes that
// width for all three water series instead of letting each infer its own.
// Day granularity only, implicitly: the norm this is the excess over exists on no other bucket, so
// at hourly granularity every bucket filters out and the series draws nothing.
export function buildWaterOverNormSeriesData(buckets: ProductivityBucket[]): Array<[number, number, number]> {
    return buckets
        .filter((bucket) => bucket.water_per_pig !== null && bucket.norm_water_per_pig !== null && bucket.water_per_pig > bucket.norm_water_per_pig)
        .map((bucket) => [bucketTimestamp(bucket), bucket.norm_water_per_pig as number, bucket.water_per_pig as number]);
}

/**
 * The point range all three water series declare, so they draw at identical widths and stack
 * exactly on top of each other. Left to Highcharts each series infers its own from its own closest
 * pair of points, and the series holding only the over-norm days - a handful of scattered readings -
 * infers columns wide enough to straddle its neighbours instead of capping its own day.
 *
 * Derived from the buckets that actually carry a reading rather than from the granularity, because
 * a Section whose Sensor reported on thirty days of a year is drawing thirty columns, not three
 * hundred and sixty-five: sized at one day each they are a pixel wide and unreadable, which is the
 * same answer Highcharts arrives at on its own and the reason this only has to unify it.
 */
export function waterColumnRange(buckets: ProductivityBucket[], granularity: ProductivityGranularity): number {
    const actualKey = waterActualKey(granularity);
    const reported = buckets.filter((bucket) => bucket[actualKey] !== null || bucket.norm_water_per_pig !== null).map(bucketTimestamp);
    reported.sort((left, right) => left - right);

    let closest = Infinity;
    for (let index = 1; index < reported.length; index += 1) {
        const gap = reported[index] - reported[index - 1];
        if (gap > 0 && gap < closest) closest = gap;
    }
    // A single reading (or none) leaves nothing to measure, and a gap narrower than the bucket
    // width cannot be real - fall back to the width the granularity itself implies.
    return Number.isFinite(closest) ? Math.max(closest, BUCKET_PERIOD_MS[granularity]) : BUCKET_PERIOD_MS[granularity];
}

/**
 * The width one of this metric's columns covers: the bucket the chart is drawn at, unless the metric
 * is published at a coarser grain than that, in which case its own. A dead count is a day's tally,
 * so at hourly it is drawn a day wide - left to Highcharts every column series on the axis infers
 * its width from the axis' closest pair of points, which at hourly is an hour, and a day's figure
 * then claimed one hour out of the twenty-four it is actually about.
 */
export function metricColumnRange(key: MetricKey, granularity: ProductivityGranularity): number {
    return BUCKET_PERIOD_MS[PRODUCTIVITY_METRICS[key].publishedGrain === 'day' && granularity === 'hour' ? 'day' : granularity];
}

export function buildDeadCountSeriesData(
    buckets: ProductivityBucket[],
    productionGroups: ProductivityProductionGroup[],
    granularity: ProductivityGranularity
): Array<[number, number | null]> {
    return buildBucketSeriesData(bucketsStating(buckets, 'deadCount', granularity), productionGroups, (bucket) => bucket.dead_count);
}
